/**
 * Calculate basket prices client-side based on items and existing prices
 * This provides instant price updates without API calls
 */
export function calculateBasketPrices(basket) {
  if (!basket.items || basket.items.length === 0) {
    return null;
  }

  // Calculate subtotal from items
  const subtotal = basket.items.reduce((sum, item) => {
    return sum + (item.total || 0);
  }, 0);

  // Use existing prices as base for tax rates and fees
  const existingPrices = basket.prices;

  // If no existing prices, return minimal prices (will be updated by API on checkout)
  if (!existingPrices) {
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax: 0,
      discountprice: 0,
      total: Math.round(subtotal * 100) / 100,
      tip: 0,
      fee: 0,
      taxrate: 0,
      crvtax: 0,
      itemfee: 0,
      totalitemfee: 0,
      totalextrafee: 0,
      extrafees: [],
    };
  }

  // Calculate tax based on taxrate (if available)
  const taxrate = existingPrices.taxrate || 0;
  const tax = subtotal * (taxrate / 100);

  // Keep existing fee, crvtax, and extra fees (these are usually fixed or store-specific)
  const fee = existingPrices.fee || 0;
  const crvtax = existingPrices.crvtax || 0;
  const totalextrafee = existingPrices.totalextrafee || 0;
  const totalitemfee = existingPrices.totalitemfee || 0;
  const extrafees = existingPrices.extrafees || [];

  // Calculate discount (keep existing discount if coupon is applied)
  const discountprice =
    basket.discountcode && existingPrices.discountprice
      ? existingPrices.discountprice
      : 0;

  // Calculate tip
  let tip = 0;
  if (basket.tipType === 'rate' && basket.tiprate > 0) {
    // Calculate tip based on subtotal after discount
    const subtotalAfterDiscount = subtotal - discountprice;
    tip = subtotalAfterDiscount * (basket.tiprate / 100);
  } else if (basket.tipType === 'custom') {
    tip = basket.tip || 0;
  }

  // Calculate total
  const total =
    subtotal +
    tax +
    fee +
    crvtax +
    totalextrafee +
    tip -
    discountprice;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    tax: Math.round(tax * 100) / 100,
    discountprice: Math.round(discountprice * 100) / 100,
    total: Math.round(total * 100) / 100,
    tip: Math.round(tip * 100) / 100,
    fee: fee,
    taxrate: taxrate,
    crvtax: crvtax,
    itemfee: existingPrices?.itemfee || 0,
    totalitemfee: totalitemfee,
    totalextrafee: totalextrafee,
    extrafees: extrafees,
  };
}

